import { useMemo } from 'react';
import * as R from 'remeda';
import { Annotation } from '@rossum/api-client/annotations';
import { Document } from '@rossum/api-client/documents';
import { GridColDef } from '@rossum/ui/x-data-grid-pro';
import { useCalculatePreview } from '../hooks/useCalculatePreview';
import { useCalculateRulesPreview } from '../hooks/useCalculateRulesPreview';
import { RowModel } from './helpers';
import { transformDataToRowsFormula } from './transformDataToRowsFormula';
import { transformDataToRowsRules } from './transformDataToRowsRule';

type UseRowsParams = {
  pageSize: number;
  annotations: Annotation[] | undefined;
  documents: Document[] | undefined;
  columns: Array<GridColDef<RowModel>>;
  evaluationQueriesFormula?: ReturnType<typeof useCalculatePreview>;
  evaluationQueriesRules?: ReturnType<typeof useCalculateRulesPreview>;
  showPreviewLine?: boolean;
};

type TransformDataToRowsParams = {
  columns: Array<GridColDef<RowModel>>;
  annotations: Annotation[];
  documentsMap: Record<string, string>;
  evaluationQueriesFormula: ReturnType<typeof useCalculatePreview>;
  evaluationQueriesRules: ReturnType<typeof useCalculateRulesPreview>;
  showPreviewLine: boolean;
};

export const transformDataToRows = (
  params: TransformDataToRowsParams
): Array<RowModel> => {
  if (
    params.evaluationQueriesFormula &&
    params.evaluationQueriesFormula.length
  ) {
    return transformDataToRowsFormula(params);
  }
  if (params.evaluationQueriesRules && params.evaluationQueriesRules.length) {
    return transformDataToRowsRules(params);
  }

  return [];
};

export const useRows = ({
  columns,
  annotations,
  documents,
  evaluationQueriesFormula,
  evaluationQueriesRules,
  showPreviewLine = false,
}: UseRowsParams): Array<RowModel> => {
  const documentsMap: Record<string, string> = useMemo(
    () =>
      R.fromEntries(
        documents?.map(({ url, originalFileName }) => [
          url,
          originalFileName ?? '',
        ]) ?? []
      ),
    [documents]
  );

  return transformDataToRows({
    columns,
    annotations: annotations ?? [],
    showPreviewLine,
    documentsMap,
    evaluationQueriesFormula: evaluationQueriesFormula ?? [],
    evaluationQueriesRules: evaluationQueriesRules ?? [],
  });
};
